/**
 * SORT — N items into K labelled bins.
 *
 * Sibling of Ordering, and the difference is the key: Ordering stores a PERMUTATION of item ids,
 * Sort stores a PARTITION — which bins each item is allowed in. Order matters there, membership
 * matters here, and holding that line is what keeps a Sort from grading the sequence inside a bin
 * and marking a student wrong for something the question never asked.
 *
 * Built by porting Ordering wherever possible: the flat `items[]` and everything hanging off it
 * (media options, versions, scenarios), the drag layer, the ARIA announcements. See
 * [[project_bin_sort_block]] for the settled design — this file grows phase by phase against it.
 *
 * ## Phase 0
 *
 * Type registered everywhere a block type has to be registered (schema enum, backend DTO, frontend
 * union, library, renderer) and a card that mounts, selects and deletes. No authoring, no student
 * surface, no evaluation yet — those are the phases after this, and a block that cannot be added to
 * a task cannot be developed against.
 */
import {
  acceptedBins, addSortVersion, angleModeFor, applySortVersion, branchSetsToCheck, checkBranchCoverage,
  displayEvaluation,
  checkOptionFormulaIssues, emptySortKey,
  enumerateFormulaValues, evaluateEquation, evaluateSort, forEachRoll, getEquationVariableNames,
  sortBranchIssue,
  markScenarioReviewed, reconcileKey, removeSortVersion, sweepFeasibility, unkeyedItems, withBinRemoved, withItemPlaced,
  withItemRemoved, writeSortVersion,
  type AngleMode, type FormulaVariable,
  type FeedbackState, type GradingMode, type SortAnswer, type SortBin, type SortItem, type SortKey,
  sortReachability, unreviewedCount,
  type ConditionalSortBranch, type ScenarioSortBranch, type SortFeedback, type SortLayout,
  type SortVersion, type DrivenFollower,
} from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Columns3, GripVertical } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useArmedDelete } from './block-actions';
import { applyIssuePolicy, type BlockIssue, duplicateKeyIgnoringBlank, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, collapsedIssueBar, collapsedModeBar, type TestBarChip } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { FeedbackCard, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { stripHtmlForLabel } from './formula-plaintext';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { MAX_HORIZONTAL_BINS, SortAnswerDisplay } from './sort-answer-display';
import { SortBinsEditor, UNSORTED } from './sort-bins-editor';
import { SortTriggerEditor, nextSortTrigger, sortTargetedTitle, sortTriggerKind } from './sort-trigger';
import { useBlockVersions, useTestResetOnVersionChange } from './use-block-versions';
import { useDrivenVersionProjection } from './use-driven-versions';
import { hasEmptyFeedbackContent, normalizeStandardFeedback, positiveTriggerForMode, standardFeedbackDefaults, useInputBlockFeedback, type ExtraDefaultState } from './use-input-block-feedback';
import { useScenarioFollower } from './use-scenario-follower';
import { useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useVariableMechanismAllowed } from './use-variable-affordance';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { BlockPreviewProvider, BlockQuestion, formulaToRichText, hasRollableVariables, htmlHasVariableChips, processVariableChipHTML, rollVariableValues, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { VersionSelector } from './version-selector';

import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/react';

import { CheckboxRow } from '@/components/ui/checkbox-row';
import { RadioGroup } from '@/components/ui/radio-group';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskPreviewRoll, useTaskVariables } from '@/contexts/task-variables-context';
import { sortChips } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { effectiveSetting, settingAvailable } from '@/lib/mode-gated-settings';

/** Two bins and three items: the smallest sort that is actually a sort. A single bin is a list,
 *  and K=2 with one bin unlabelled is MCQ multi-select wearing a costume. */
const MIN_BINS = 2;
/** Six, and the horizontal layout refuses above four — the same count-gating Ordering uses for its
 *  circular shape, for the same reason: a layout that cannot hold the content is not offered. */
const MAX_BINS = 6;

const LAYOUTS: SortLayout[] = ['horizontal', 'vertical', 'responsive'];

/** The only cycle a locked no-answer or partial card offers: a teacher decides whether leaving the
 *  sort unfinished counts against the student, and nothing else about the state is theirs to set. */
const NEUTRAL_OR_INCORRECT = ['neutral', 'incorrect'] as const satisfies readonly FeedbackState['evaluation'][];

/** The plain string a label or item renders to for one roll — what a student would actually read,
 *  which is the only thing a duplicate check can honestly compare. */
function renderedText(html: string, rolled: Record<string, string>): string {
  return stripHtmlForLabel(processVariableChipHTML(html, rolled)).trim();
}

/**
 * What an item actually DISPLAYS — identical contract to `orderingItemDisplayText`.
 *
 * In equations mode the formula is evaluated and the RESULT is shown, a plain formatted number and
 * never the formula string. A media item is untouched: its `text` was never student-facing to
 * begin with, and media and formula are mutually exclusive anyway. Blank or unevaluable stays
 * blank — obviously incomplete, never a placeholder.
 */
function sortItemDisplayText(
  item: SortItem,
  itemsAsEquations: boolean | undefined,
  resolvedVariables: Record<string, number>,
  locale: string,
  angleMode: AngleMode,
): string {
  if (!itemsAsEquations || item.media) return item.text;
  if (!item.formula?.trim()) return '';
  const v = evaluateEquation(item.formula, resolvedVariables, angleMode);
  return v === null ? '' : fmtLocale(v, locale);
}

/**
 * SETUP PROBLEMS, and the duplicate rule is the one worth reading.
 *
 * Identical items are NOT automatically wrong here, which is where Sort departs from Ordering.
 * A teacher may legitimately say "put all the balls in column A and the cubes in column B" with
 * one repeated image — the owner's own counter-example. Three cases:
 *
 * | items              | accepted bins | order within bins | verdict                        |
 * |--------------------|---------------|-------------------|--------------------------------|
 * | identical          | the same      | off               | FINE: interchangeable          |
 * | identical          | the same      | on                | ERROR: which ball is slot 1?   |
 * | identical          | different     | either            | ERROR: unanswerable            |
 *
 * The real reason to error rather than tolerate the last two: in every ALLOWED state the evaluator
 * stays a straight comparison. Those are exactly the cases that would otherwise force evaluation
 * into a bipartite match on every attempt ("is there SOME assignment of the identical items that
 * satisfies the key"). Flagging at authoring time keeps that out of the runtime forever.
 */
/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `sortIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function sortBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return sortIssues(initData((block.data ?? {}) as Record<string, unknown>, ctx.t), ctx);
}

export function sortIssues(d: SortBlockDataLocal, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({
      key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true,
      category: categoryQuestion, detail: t('blocks.issues.questionBlank'),
    });
  }

  // A bin with no name is not a bin a student can sort INTO — it is the degenerate MCQ-multi case
  // the labels are what distinguish Sort from.
  const unnamedBins = d.bins.filter(b => !b.label.replace(/<\/?p>/g, '').trim());
  if (unnamedBins.length > 0) {
    issues.push({
      key: 'bin-label-blank', fields: unnamedBins.map(b => b.id),
      severity: 'error', kind: 'incompletion',
      category: categoryAnswerSetup, detail: t('blocks.sort.binLabelBlank'),
    });
  }

  /* In equations mode the content is the FORMULA, so an item with text and no formula is the blank
     one. `text` is deliberately not cleared by the toggle, so testing it here would call a freshly
     switched block complete when not one formula had been authored. */
  const blankItems = d.itemsAsEquations
    ? d.items.filter(i => !i.media && !i.formula?.trim())
    : d.items.filter(i => !i.media && !i.text.replace(/<\/?p>/g, '').trim());
  if (blankItems.length > 0) {
    /* LITERAL KEYS, two pushes rather than one with a ternary. The issue-inventory scanner reads
       these as source text and cannot follow a key chosen at runtime — a ternary here silently
       drops the wording out of the generated inventory. */
    issues.push(d.itemsAsEquations ? {
      key: 'item-formula-blank', fields: blankItems.map(i => i.id),
      severity: 'error', kind: 'incompletion',
      category: categoryAnswerSetup, detail: t('blocks.sort.itemFormulaBlankWarning'),
    } : {
      key: 'item-blank', fields: blankItems.map(i => i.id),
      severity: 'error', kind: 'incompletion',
      category: categoryAnswerSetup, detail: t('blocks.sort.itemBlank'),
    });
  }

  /**
   * PER VERSION, from here down.
   *
   * These checks read version-resolved data, so a banked block was only ever validated for the
   * version on screen: a key left unfinished in version 3 stayed invisible until someone happened
   * to step to it. The keys must differ per version or two versions' problems collapse into one
   * issue, and the message has to say WHICH version or it names a setup nobody can find.
   *
   * `branchSetsToCheck` does the same job for the BRANCH sets below; this loop is the half it
   * does not cover, because Sort's key and its duplicate rule are not branch conditions.
   */
  for (const scope of versionScopes(d)) {
    issues.push(...keyIssues(scope, d, t, variables));
  }

  /**
   * TWO CARDS WITH ONE CONDITION.
   *
   * Targeted states fire in PARALLEL, so two cards naming the same condition both fire and the
   * student gets the same mistake explained twice. The repeatable kinds are the ones that can
   * collide — Item in Bin and Custom Arrangement — because the structural kinds are already
   * one-apiece through `usedKinds`.
   *
   * A defect, not an incompletion: two identical conditions are a contradiction a teacher has to
   * resolve, not a blank waiting to be filled. The sibling case — a targeted state that duplicates
   * the CORRECT answer — needs nothing here: reachability only ever matches targeted triggers
   * against WRONG arrangements, so one authored on the key matches none and already reports
   * itself unreachable.
   *
   * No other block checks this yet; it is worth lifting when one of them wants it.
   */
  const targetedStates = d.feedback.states.filter(st => st.type === 'targeted');
  for (const group of findDuplicateGroups(targetedStates, st => st.trigger)) {
    issues.push({
      key: `duplicate-targeted-${group.map(st => st.id).join('-')}`,
      fields: group.map(st => st.id),
      severity: 'error', kind: 'defect', category: categoryFeedback,
      detail: t('blocks.sort.duplicateTargeted'),
    });
  }

  // ── Duplicate bin labels, including the ones that only COLLIDE on some rolls ─
  issues.push(...binLabelCollisions(d, variables, t));

  /* A blank Label on a media item silently empties every sentence feedback, Flow and reports
     build about that item — an error however it got there, and still an incompletion, because
     severity is how loudly and `kind` is when. */
  const mediaMissingLabel = d.items.filter(i => i.media && !i.text.replace(/<[^>]*>/g, '').trim());
  if (mediaMissingLabel.length > 0) {
    issues.push({
      key: 'media-label-blank', fields: mediaMissingLabel.map(i => i.id),
      severity: 'error', kind: 'incompletion',
      category: categoryAnswerSetup, detail: t('blocks.sort.itemMediaLabelBlank'),
    });
  }

  /* A DEFECT, not an incompletion: an image with no alt text is not an unfinished thought, it is
     a block a screen-reader user cannot answer. Audio has no transcript requirement here, the same
     accepted limitation MCQ's and Ordering's media options carry. */
  const imagesMissingAlt = d.items.filter(i => i.media?.kind === 'image' && !i.media.altText?.trim());
  if (imagesMissingAlt.length > 0) {
    issues.push({
      key: 'media-alt-text-missing', fields: imagesMissingAlt.map(i => i.id),
      severity: 'error', kind: 'defect',
      category: categoryAnswerSetup, detail: t('blocks.sort.itemMediaAltTextRequired'),
    });
  }

  /* A row the projection COPIED rather than a teacher DECIDED. The pager shows a count beside the
     row you are on; this is the half that reaches the publication gate, so a teacher who never
     pages to scenario 5 still learns it was never reviewed. */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup, detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }

  // ── Alternative answer setups ───────────────────────────────────────────────
  /* THROUGH THE SHARED LOOP, which exists because "seven copies of remember to loop the versions
     is six chances to forget" — and I was the eighth block and forgot. It also reaches a
     follower's nested branching, which a hand-rolled loop over `d.conditionalBranches` misses
     entirely. */
  for (const set of d.gradingMode === 'graded' ? branchSetsToCheck<ConditionalSortBranch>(d) : []) {
    const branches = set.branches ?? [];
    if (branches.length === 0) continue;
    const categoryAlternatives = t('blocks.areas.alternativeAnswerSetup');
    /** Keys must differ per set, or two sets' gaps collapse into one issue. */
    const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
      : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
    /** And the message must say WHICH one, or it names a condition nobody can find. */
    const vlabel = (detail: string) => (set.versionIndex !== null
      ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
      : set.scenarioIndex !== null
        ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
        : detail);
    const equation = set.equation?.trim();
    if (!equation) {
      issues.push({
        key: vkey('no-equation'), severity: 'error', kind: 'incompletion',
        category: categoryAlternatives, detail: vlabel(t('blocks.branching.diagnosticNoEquation')),
      });
    } else if (evaluateEquation(equation, minSampleValues(variables)) === null) {
      issues.push({
        key: vkey('invalid-equation'), severity: 'error', kind: 'defect',
        category: categoryAlternatives, detail: vlabel(t('blocks.branching.equationInvalidWarning')),
      });
    } else {
      /* A value the student can draw that NO branch claims leaves the block with no key at all for
         that roll — `resolveEffectiveKey` fails closed, so correct becomes unreachable rather than
         silently falling back to a key nobody is reviewing. */
      const coverage = checkBranchCoverage(branches, enumerateFormulaValues(equation, variables));
      const gaps = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
      const overlaps = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
      if (gaps.length > 0) {
        issues.push({
          key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryAlternatives,
          detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gaps, t, 'gap') })),
        });
      }
      if (overlaps.length > 0) {
        issues.push({
          key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryAlternatives,
          detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlaps, t, 'overlap') })),
        });
      }
    }
    /* A branch whose own key never places every item can never grade correctly however the
       equation resolves — the same guard `keyIsComplete` is inside the evaluator, surfaced where
       a teacher can act on it. */
    for (const [index, branch] of branches.entries()) {
      if (sortBranchIssue(branch, d.items)) {
        issues.push({
          key: vkey(`branch-${branch.id}-incomplete-key`), fields: [branch.id],
          severity: 'error', kind: 'incompletion', category: categoryAlternatives,
          detail: vlabel(t('blocks.sort.branchKeyIncomplete', { number: index + 1 })),
        });
      }
    }
  }

  /* The top-level set only, because that is the only set this block can author yet. Phase 5 adds
     branches and this reads through `activeFeedbackForBranch`, as Ordering's does. */
  const hasMissingFeedbackText = hasEmptyFeedbackContent(d.feedback.states);
  if (hasMissingFeedbackText) {
    issues.push({
      key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion',
      category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText'),
    });
  }

  return issues;
}

/* THROUGH THE REGISTRY, never off the field: the stored value survives a mode switch and its
   effect must not, or the duplicate rule would call a key broken in a mode that does not grade
   order at all. */
const ordered = (d: SortBlockDataLocal, stored: boolean | undefined) =>
  effectiveSetting('orderWithinBins', d.gradingMode, !!stored);

/** One entry per answer setup to validate: every version of a banked block, or the block itself. */
function versionScopes(d: SortBlockDataLocal): { index: number | null; key: SortKey; orderWithinBins: boolean }[] {
  if (!d.versions || d.versions.length === 0) {
    return [{ index: null, key: d.key, orderWithinBins: ordered(d, d.orderWithinBins) }];
  }
  return d.versions.map((v, i) => ({ index: i + 1, key: v.key, orderWithinBins: ordered(d, v.orderWithinBins) }));
}

function keyIssues(
  scope: { index: number | null; key: SortKey; orderWithinBins: boolean },
  d: SortBlockDataLocal,
  t: (k: string, opts?: Record<string, unknown>) => string,
  /** Needed only by the equations-mode collision sweep, which enumerates their declared domains. */
  variables: TaskVariable[] = [],
): BlockIssue[] {
  const issues: BlockIssue[] = [];
  /* Derived here rather than passed in: the issue-inventory scanner reads these as source text
     and cannot follow a category that arrives as a parameter — the same thing that hid three
     checks when `binLabelCollisions` took one. */
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const vkey = (k: string) => (scope.index === null ? k : `v${scope.index}-${k}`);
  const vlabel = (detail: string) => (scope.index === null
    ? detail
    : t('blocks.branching.versionScopedIssue', { number: scope.index, detail }));

  /* An item the key says nothing about accepts NO bin — `acceptsBin` fails closed — so the block
     is unanswerable until every item is placed. Graded only: the other modes author no key, so a
     missing one is the mode working rather than a setup left unfinished. */
  if (d.gradingMode === 'graded') {
    const unplaced = unkeyedItems(scope.key, d.items);
    if (unplaced.length > 0) {
      issues.push({
        key: vkey('key-incomplete'), fields: unplaced,
        severity: 'error', kind: 'incompletion',
        /* The collapsed card's own body already prints this count for the version on screen, so
           that one steps aside. A problem in a version the card is NOT showing has no such cover
           and must still reach the bar. */
        shownInPlace: scope.index === null,
        category: categoryAnswerSetup, detail: vlabel(t('blocks.sort.unplacedNote', { count: unplaced.length })),
      });
    }
  }

  // ── Duplicate items, by the three-case rule above ───────────────────────────
  const sameBins = (a: SortItem, b: SortItem) => {
    const left = acceptedBins(scope.key, d.bins, a.id).join(',');
    return left === acceptedBins(scope.key, d.bins, b.id).join(',');
  };
  /*
   * EQUATIONS MODE ASKS THE SAME THREE QUESTIONS, of a different kind of sameness.
   *
   * Ordering treats any formula collision as an error outright. Sort must not: two items that
   * resolve alike are only a problem when the key can TELL THEM APART — the identical-items table
   * above applies unchanged, and "same value, same bins, order off" is the interchangeable case a
   * teacher is entitled to author. So the collision groups are fed through the very same
   * `sameBins`/`orderWithinBins` verdict rather than flagged on sight.
   *
   * A formula that will not evaluate is separate and unconditional: it has no value at all, so
   * there is nothing for a student to read and no case in which it is fine.
   */
  const duplicateGroups = d.itemsAsEquations
    ? (() => {
        const { issues: formulaIssues } = checkOptionFormulaIssues(
          d.items.filter(i => !i.media), variables as unknown as FormulaVariable[],
          id => angleModeFor(d, `items.${id}.formula`),
        );
        for (const issue of formulaIssues.filter(i => i.kind === 'unevaluable')) {
          issues.push({
            key: vkey(`item-unevaluable-${issue.optionIds.join('-')}`), fields: issue.optionIds,
            severity: 'error', kind: 'defect', category: categoryAnswerSetup,
            detail: vlabel(t('blocks.sort.itemFormulaUnevaluable')),
          });
        }
        return formulaIssues
          .filter(i => i.kind === 'collision')
          .map(i => i.optionIds.map(id => d.items.find(it => it.id === id)!).filter(Boolean));
      })()
    : findDuplicateGroups(d.items.filter(i => !i.media), i => duplicateKeyIgnoringBlank(i.text));

  /** What the message calls the offending pair — its formula in equations mode, its text otherwise. */
  const groupName = (i: SortItem) => stripHtmlForLabel(d.itemsAsEquations ? (i.formula ?? '') : i.text);

  for (const group of duplicateGroups) {
    if (group.length < 2) continue;
    const interchangeable = group.every(i => sameBins(i, group[0]!));
    if (!interchangeable) {
      /* A DIRECT `t()` on each branch. The inventory scanner resolves `vlabel(t('key', ...))` by
         reading the source and gives up on anything it has to evaluate — a conditional inside the
         call drops the wording from the generated doc without failing anything. */
      const common = {
        key: vkey(`duplicate-split-${group.map(i => i.id).join('-')}`), fields: group.map(i => i.id),
        severity: 'error' as const, kind: 'defect' as const, category: categoryAnswerSetup,
      };
      issues.push(d.itemsAsEquations
        ? { ...common, detail: vlabel(t('blocks.sort.formulaItemsDifferentBins', { text: groupName(group[0]!) })) }
        : { ...common, detail: vlabel(t('blocks.sort.duplicateItemsDifferentBins', { text: groupName(group[0]!) })) });
    } else if (scope.orderWithinBins) {
      /* NAMES THE TOGGLE, because this error appears the instant it is flipped on and would
         otherwise look like it came from nowhere — the key was valid a moment earlier. */
      const common = {
        key: vkey(`duplicate-ordered-${group.map(i => i.id).join('-')}`), fields: group.map(i => i.id),
        severity: 'error' as const, kind: 'defect' as const, category: categoryAnswerSetup,
      };
      issues.push(d.itemsAsEquations
        ? { ...common, detail: vlabel(t('blocks.sort.formulaItemsOrdered', { text: groupName(group[0]!) })) }
        : { ...common, detail: vlabel(t('blocks.sort.duplicateItemsOrdered', { text: groupName(group[0]!) })) });
    }
    // else: identical AND interchangeable. Legitimate, and deliberately silent.
  }

  // Two items on the same asset: a student cannot tell which physical item they are moving, which
  // is the same fairness problem duplicate text is, and the same three-case rule applies.
  for (const group of findDuplicateGroups(d.items.filter(i => i.media), i => i.media!.assetId)) {
    const interchangeable = group.every(i => sameBins(i, group[0]!));
    if (!interchangeable || scope.orderWithinBins) {
      issues.push({
        key: vkey(`duplicate-media-${group.map(i => i.id).join('-')}`), fields: group.map(i => i.id),
        severity: 'error', kind: 'defect', category: categoryAnswerSetup,
        detail: vlabel(t('blocks.sort.duplicateItemMedia')),
      });
    }
  }

  return issues;
}

/**
 * Two bins a student cannot tell apart — and the interesting half is POTENTIAL collision.
 *
 * Two labels carrying different variables may roll to the same string on some draws and not
 * others. A check that only compared the authored text would call them distinct and be wrong for
 * one student in six. So the chips are swept exhaustively, and when the space is too wide to sweep
 * the honest report is `unverified` rather than silence — silence there reads as "checked, fine",
 * which is the one thing a teacher must not be told.
 */
function binLabelCollisions(
  d: SortBlockDataLocal,
  variables: TaskVariable[],
  t: (k: string, opts?: Record<string, unknown>) => string,
): BlockIssue[] {
  /* Derived here rather than passed in: the issue-inventory scanner reads these as source text and
     cannot follow a category that arrives as a parameter, so three real checks reported no area. */
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const labelled = d.bins.filter(b => b.label.replace(/<\/?p>/g, '').trim());
  if (labelled.length < 2) return [];

  const names = variableChipNames(labelled.map(b => b.label).join(' '));
  const formulaVars = variables as unknown as FormulaVariable[];

  /* No chips: one comparison settles it, and the labels are literally identical. */
  if (names.length === 0) {
    return findDuplicateGroups(labelled, b => renderedText(b.label, {})).map(group => ({
      key: `duplicate-bin-${group.map(b => b.id).join('-')}`,
      fields: group.map(b => b.id),
      severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.sort.duplicateBinLabels', { label: renderedText(group[0]!.label, {}) }),
    } satisfies BlockIssue));
  }

  const feasible = sweepFeasibility(names, formulaVars);
  if (feasible !== 'ok') {
    return [{
      key: 'bin-labels-unswept', severity: 'warning', kind: 'unverified', category: categoryAnswerSetup,
      detail: t('blocks.sort.binLabelsUnswept'),
    }];
  }

  /* The FIRST roll that collides is enough: one draw where two bins read the same is a broken
     question for whoever draws it, and reporting every colliding roll would say the same thing
     many times over. */
  let collided: [string, string, string] | null = null;
  forEachRoll(names, formulaVars, (rolled) => {
    const seen = new Map<string, string>();
    for (const bin of labelled) {
      const text = renderedText(bin.label, rolled);
      const prior = seen.get(text);
      if (prior !== undefined) { collided = [prior, bin.id, text]; return 'stop'; }
      seen.set(text, bin.id);
    }
    return undefined;
  });

  if (!collided) return [];
  const [a, b, text] = collided as [string, string, string];
  return [{
    key: `duplicate-bin-rolled-${a}-${b}`, fields: [a, b],
    severity: 'error', kind: 'defect', category: categoryAnswerSetup,
    detail: t('blocks.sort.duplicateBinLabels', { label: text }),
  }];
}

const sortIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();

/** The drag handle a targeted card hangs off. Every block carries its own copy of this six-line
 *  wrapper; consolidating them is its own sweep, not this block's to start. */
function SortableFeedbackCard({ sortId, ...cardProps }: FeedbackCardProps & { sortId: string }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical aria-hidden='true' className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

interface SortBlockDataLocal extends DrivenFollower {
  question: string;
  items: SortItem[];
  bins: SortBin[];
  key: SortKey;
  layout: SortLayout;
  inverted: boolean;
  itemsAcross: boolean;
  orderWithinBins: boolean;
  gradingMode: GradingMode;
  feedback: SortFeedback;
  /** The question bank, when one has been authored. Absent on every block that has never had
   *  one, which is why applying a version is identity for them. */
  versions?: SortVersion[];
  /** The shared expression every branch compares against. */
  conditionalEquation?: string;
  /** Each carries a WHOLE alternate key and its own feedback — what is correct genuinely differs
   *  per branch, so what the block says about it has to be able to differ too. */
  conditionalBranches?: ConditionalSortBranch[];
  /** Banked under its own key while the toggle is off, so turning it back on restores the setup
   *  rather than starting over. Inert data: nothing reads it but the toggle. */
  conditionalBranchesDraft?: { equation: string; branches: ConditionalSortBranch[] };
  /** The leader this block follows, when it follows one. A follower holds no bank: a scenario is
   *  one stem with many setups, a bank is many stems with one each, and they cannot both be the
   *  answer key. */
  followsBlockId?: string;
  /** One setup per variant the leader can draw — the scenario twin of `conditionalBranches`. */
  scenarioBranches?: ScenarioSortBranch[];
  /** Every item's `formula` is evaluated and the RESULT is what a student reads, instead of its
   *  `text` — identical contract to `OrderingBlockData.itemsAsEquations`. Membership is untouched
   *  either way: an item is keyed by `id`, never by what it displays. */
  itemsAsEquations?: boolean;
  /** Per-field angle modes, keyed by `fieldId` — the branch equation and now each item's formula. */
  angleModes?: Record<string, AngleMode>;
  angleMode?: AngleMode;
}

const newId = () => crypto.randomUUID();

/** Seeded with real NAMES, not blanks. An unnamed bin is a `bin-label-blank` incompletion, so a
 *  freshly added Sort used to arrive already reporting two of them; and the collapsed card now
 *  shows the arrangement, which needs something to label the columns with. */
function defaultBins(t: (k: string, o?: Record<string, unknown>) => string): SortBin[] {
  return [
    { id: 'default-bin-1', label: t('blocks.sort.defaultBinName', { number: 1 }) },
    { id: 'default-bin-2', label: t('blocks.sort.defaultBinName', { number: 2 }) },
  ];
}

/**
 * Two per bin, PLACED, with the source empty.
 *
 * Everything-unsorted was the honest empty state and the wrong default. The key that places every
 * item is not one possible answer among many, it is what a finished Sort always looks like — so
 * starting unplaced made the first job on every new block undoing the default, and starting with
 * the one thing a teacher cannot leave undone unflagged.
 *
 * Two each, matching the two options MCQ opens with and the two items Ordering does. The items are
 * blank, so this places nothing a teacher has to disagree with: it is the SHAPE that is seeded,
 * the same way two blank options seed MCQ's.
 */
function defaultItems(): SortItem[] {
  return [
    { id: 'default-item-1', text: '' },
    { id: 'default-item-2', text: '' },
    { id: 'default-item-3', text: '' },
    { id: 'default-item-4', text: '' },
  ];
}

/** The seeded key: two items in each seeded bin, which is what `defaultItems` is counted for. */
function defaultKey(): SortKey {
  return { placements: { 'default-bin-1': ['default-item-1', 'default-item-2'], 'default-bin-2': ['default-item-3', 'default-item-4'] } };
}

/** Raw stored data to the shape every check here reads. Exported as `sortData` for the one
 *  caller outside this file that has to normalise the same way the block does. */
export { initData as sortData };

/**
 * THE PARTIAL STATE, defined once for the two places that must agree about it.
 *
 * `incomplete_answer` is an extra default here as it is on FITB — a sort can be half done, and
 * "left three items in the source" is a different thing to say than "got it wrong". The editor's
 * `useInputBlockFeedback` and `initData` below both seed from this, because they normalise the
 * same stored feedback for different readers: the panel the teacher edits, and the data the
 * grader runs on. A set that differs between them is a state the panel offers and the student
 * surface cannot fire, which is the shape of the defect this function exists to prevent.
 */
function sortExtraDefaults(t: (k: string, o?: Record<string, unknown>) => string): ExtraDefaultState[] {
  return [{
    trigger: 'incomplete_answer',
    title: t('blocks.feedbackDefaults.incompleteAnswerTitle'),
    content: `<p>${t('blocks.feedbackDefaults.incompleteAnswerContent')}</p>`,
    evaluation: 'neutral',
  }];
}

function initData(
  raw: Record<string, unknown>,
  t: (k: string, o?: Record<string, unknown>) => string,
): SortBlockDataLocal {
  const rawItems = Array.isArray(raw.items) ? raw.items as Partial<SortItem>[] : [];
  const rawBins = Array.isArray(raw.bins) ? raw.bins as Partial<SortBin>[] : [];
  const items: SortItem[] = rawItems.length
    // Positional fallback id for anything saved without one: deterministic, so identity survives a
    // re-render. A random id here would churn every frame.
    ? rawItems.map((i, idx) => ({ id: i.id ?? `item-${idx}`, text: i.text ?? '', formula: i.formula, media: i.media }))
    : defaultItems();
  const bins: SortBin[] = rawBins.length >= MIN_BINS
    ? rawBins.map((b, idx) => ({ id: b.id ?? `bin-${idx}`, label: b.label ?? '' }))
    : defaultBins(t);
  /* A block that has never been saved gets the seeded key; one that HAS is taken at its word,
     including an empty key, because a teacher who cleared it meant to. `raw.key` being absent is
     the only signal that separates the two. */
  const rawKey = (raw.key as SortKey | undefined) ?? (rawItems.length ? emptySortKey() : defaultKey());
  /* Hoisted out of the object below because `feedback` reads it: the positive slot is named after
     the mode (`correct` / `any_answer` / `any_response`), so the default set cannot be built
     without knowing which mode this block is in. */
  const gradingMode = (['graded', 'survey', 'optional'].includes(raw.gradingMode as string)
    ? raw.gradingMode : 'graded') as GradingMode;
  return {
    question: (raw.question as string) ?? '',
    items,
    bins,
    // Reconciled on the way in, so a key that outlived an item or a bin never renders a phantom row.
    key: reconcileKey(rawKey, items, bins),
    /* HORIZONTAL by default: a sort reads as columns, and the source belongs beside the bins
       rather than above them. */
    layout: (LAYOUTS.includes(raw.layout as SortLayout) ? raw.layout : 'horizontal') as SortLayout,
    inverted: raw.inverted === true,
    itemsAcross: raw.itemsAcross === true,
    orderWithinBins: raw.orderWithinBins === true,
    gradingMode,
    /**
     * SEEDED, not taken raw — the defect that left this block mute.
     *
     * `(raw.feedback ?? { states: [] })` handed the grader an EMPTY state list for every block
     * whose teacher had not yet edited a feedback card, because the editor seeds its defaults
     * into the hook's own React state and writes nothing to `block.data` until something
     * changes. The panel therefore listed No Answer, Incomplete, Correct and Default Incorrect
     * while `evaluateSort` had nothing to match, so `testResult` came back empty and the test
     * card and Preview showed no feedback at all — the states existed for the teacher and did
     * not exist for the student.
     *
     * Sort was the last of the thirteen input blocks seeding nothing here; the other twelve
     * normalise in their own `initData`. Same defaults and the same extra state the panel uses,
     * so the two readers of this field cannot disagree about what can fire.
     */
    feedback: normalizeStandardFeedback(raw.feedback, t, gradingMode, {
      extraDefaults: sortExtraDefaults(t),
    }) as SortFeedback,
    ...(Array.isArray(raw.versions) && raw.versions.length ? { versions: raw.versions as SortVersion[] } : {}),
    conditionalEquation: raw.conditionalEquation as string | undefined,
    conditionalBranches: raw.conditionalBranches as ConditionalSortBranch[] | undefined,
    conditionalBranchesDraft: raw.conditionalBranchesDraft as SortBlockDataLocal['conditionalBranchesDraft'],
    /* CARRIED, not enumerated away. `write` spreads this object back out through
       `writeSortVersion`, so a field this function drops is a field every subsequent edit DELETES
       from the saved block — the angle modes were being lost that way, silently, one edit after
       a teacher set one. */
    itemsAsEquations: raw.itemsAsEquations === true,
    angleModes: raw.angleModes as Record<string, AngleMode> | undefined,
    angleMode: raw.angleMode as AngleMode | undefined,
    followsBlockId: raw.followsBlockId as string | undefined,
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as string | undefined,
    scenarioBranches: raw.scenarioBranches as ScenarioSortBranch[] | undefined,
  };
}

interface SortBlockProps {
  block: Block;
  isPreview: boolean;
  experienceId: string;
  taskId?: string;
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

export default function SortBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, blocks, onRename,
  initialIsEditingFeedback, onFeedbackModeChange, mobileIsEditing,
  experienceId, taskId,
}: SortBlockProps) {
  const { t, i18n } = useTranslation();
  /* The top rung, asked once for the whole editor. It gates only whether a mechanism may be
     ADOPTED; each toggle below keeps its own `|| <already on>` so one in use can be switched
     off. See `useVariableMechanismAllowed` for why the insert gate does not imply this. */
  const mayVarMechanism = useVariableMechanismAllowed();
  const { isDeleteArmed, triggerDelete, triggerDuplicate, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const blockRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  /* `block.data` can be absent entirely — a block created from the library arrives empty, and the
     undo harness mounts one that way deliberately. Every read of it goes through `initData`, which
     is the one place that knows what a missing field means. */
  const [isEditing, setIsEditing] = useState(
    embedded || (!onSelect && !(block.data?.items as unknown[] | undefined)?.length),
  );
  const [armedBinId, setArmedBinId] = useState<string | null>(null);
  const [armedItemId, setArmedItemId] = useState<string | null>(null);
  /**
   * THE PHONE'S ONLY WAY IN.
   *
   * On a phone the canvas card does not open an editor beside itself — there is no beside. The
   * workspace holds which block is being edited and hands it down here, and a block that does not
   * read it simply never opens: tapping a Sort card on a phone did nothing at all.
   *
   * `useLayoutEffect` rather than `useEffect`, as every other block does it: the editor has to be
   * mounted in the same paint as the tap, or the card flickers back before it appears.
   */
  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);

  const sharedToolbar = useSharedEditorToolbar();
  /**
   * PER-FIELD ANGLE MODE, for the one formula field this block has: the branch control equation.
   *
   * A condition like `sin({t}) > 0.5` means different things in degrees and radians, and without
   * this the teacher cannot say which. The merge lives in the hook so no block can delete its
   * siblings' modes by writing only its own.
   */
  const angleModeProps = useBlockAngleModes(block.data, patch => onUpdate({ ...block.data, ...patch }));
  const {
    hasBank, shownVersionId, setTestVersion, editorVersionId, setEditorVersion,
  } = useBlockVersions(block);
  const questionEditorRef = useRef<Editor | null>(null);
  /** Set when the editor is opened from the card, so the question takes the cursor on arrival. */
  const shouldFocusEditor = useRef(false);
  /**
   * Whether the BANKED branch setup was banked by this editing session, which is what makes it
   * safe to restore. The other six branching blocks all carry this ref; Sort shipped without it
   * and restored `conditionalBranchesDraft` whenever the toggle came on, including a draft banked
   * weeks ago. A branch key names bins and items BY ID, so a draft that predates a new bin places
   * nothing in it and every item filed there reads as unsorted — which is exactly how it was
   * found. Stale is not recoverable by reconciling: a bin added since has no placement to repair
   * and no honest guess to make. The bank is authoring state for one sitting.
   */
  const bankedThisSessionRef = useRef(false);
  /**
   * The live test surface's own partition — a teacher trying the question out, never the key.
   *
   * Seeded at MOUNT from the last attempt, not in an effect, for the reason every restored answer
   * is: Preview remounts on navigation, so once is enough, and an effect would fight a student who
   * has already started placing. Without this the sort emptied itself every time the student walked
   * back into the task, which on a twelve-item sort is the whole exercise.
   */
  const prior = usePriorAnswer<Record<string, string[]>>();
  const [testPlacements, setTestPlacements] = useState<Record<string, string[]>>(() => prior ?? {});
  const hasPlaced = Object.values(testPlacements).some(ids => ids.length > 0);
  /* The two-value test contract every input block has: `testPlacements` is what the widget shows,
     `submittedPlacements` is what evaluation runs against. They only diverge in submit-gated mode. */
  const [submittedPlacements, setSubmittedPlacements] = useState<Record<string, string[]>>(() => prior ?? {});
  const [hasSubmitted, setHasSubmitted] = useState(prior !== undefined);
  /**
   * THE SUBMITTED ARRANGEMENT, never the draft.
   *
   * An attempt is what a student HANDED IN. Recording the live partition made it a snapshot of
   * their thinking — the record moved every time they picked something up, and the last state
   * before they wandered off became the answer. Ordering reports `submittedOrder` for the same
   * reason, and `hasPlaced` still gates it so an untouched block reports nothing at all rather
   * than an empty arrangement that reads as every item answered wrong.
   */
  useReportAnswer(isPreview && hasSubmitted && hasPlaced ? submittedPlacements : undefined);
  const [continuous, setContinuous] = useState(true);
  /** Bumped on Reset, to re-scramble the source — see `SortAnswerDisplay`. */
  /**
   * THE SCRAMBLE'S OWN GENERATION — bumped by Reset and by Reroll, which is the house rule.
   *
   * RESET IS A FRESH STUDENT. Anything random per STUDENT — the presentation shuffle — is drawn
   * again; anything drawn per task or session — the variables, the version — is not. Dropdown
   * states it outright at its own reset: "A fresh student also gets a fresh random order (each
   * real student's randomizeOrder shuffle is independent)." MCQ and Ordering do the same.
   *
   * I briefly moved the scramble to Reroll only, reasoning that two runs of one setup should be
   * comparable. That made Sort the only block of four that did not re-present on Reset, and it
   * mistook the shuffle for part of the SETUP when it is part of being a different student.
   */
  const [shuffleGeneration, setShuffleGeneration] = useState(0);
  /** Phone-only Edit/Test toggle inside the full-screen editor, the same contract MCQ's has. */
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  /**
   * PREVIEW'S ONE BOTTOM SUBMIT, which is a signal rather than a click: Preview has no per-block
   * Submit button, so the page raises this and every block snapshots at once. It reuses the same
   * `submittedPlacements`/`hasSubmitted` the live test widget already runs on — Preview is a
   * different TRIGGER for the same evaluation, never a second one.
   */
  /* Seeded when the test surface appears — the selected card, the phone Test tab, or Preview —
     so a chip resolves to a real value the first time anyone sees it rather than to nothing. */
  useEffect(() => {
    if (isSelected || isPreview || phoneTestMode === 'test') setOwnRolledValues(rollVariableValues(variables));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview, phoneTestMode]);

  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedPlacements(testPlacements);
      setHasSubmitted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);
  /** The card a teacher just added opens expanded. State, not a ref: it is read during render, and
   *  a ref mutated in a render React throws away keeps a value that render discarded. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  /** Which alternative answer setup the editor is pointed at. Clamped on read, so deleting the
   *  last branch cannot leave the pointer past the end. */
  const [activeBranchIndex, setActiveBranchIndex] = useState(0);
  const feedbackSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  /**
   * TWO ROLLS, and which one applies depends on who is looking.
   *
   * PREVIEW reads the task-level roll, shared by every block, because a task whose blocks all
   * read {n} has to agree about n. BLOCK-LEVEL TESTING keeps its own, which is the whole point of
   * it: a teacher steps through draws for the one block they are testing, and Reroll is how.
   *
   * Sort had only the first, so the editor's own test surface rendered a bin label's chip against
   * an empty roll and Reroll had nothing to re-draw.
   */
  const taskPreviewRoll = useTaskPreviewRoll();
  const [ownRolledValues, setOwnRolledValues] = useState<Record<string, string>>({});
  const rolledValues = isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues;
  /**
   * THE SAME ROLL, SAID THE WAY A SURFACE ASKS IT — `null` for display, a record for a test.
   *
   * `rolledValues` is always a record, and `{}` is not the same thing as `null`: it reads as "a
   * test surface that has rolled nothing", which leaves a variable a chip (so it looks right) and
   * tells an answer reference to resolve to a sample anyway. An unselected card showed a number for
   * a recalled answer beside a variable still drawn as a pill.
   */
  const surfaceRoll = isSelected || isPreview ? rolledValues : null;
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);

  /**
   * VERSIONS, and Sort gets them cheaply because it holds no local editing copy.
   *
   * Ordering keeps its question in state and so needs a whole re-seeding machine to switch
   * version: every field has to be lifted out of the new version and pushed back into the
   * component. Everything here is derived from `block.data` on each render, so switching version
   * is a different `useMemo` input and nothing else.
   *
   * `stored` is what is on disk; `d` is the version in force for the EDITOR. Writes go back
   * through `writeSortVersion`, which puts the edit inside the right version when there is a bank
   * and returns the data unchanged when there is not.
   */
  const stored = useMemo(() => initData(block.data ?? {}, t), [block.data, t]);

  /* One version per row of the driver's answer space. `onUpdate` rather than an `emit`: this is
     the one block with no restore bookkeeping to route through. See
     `useDrivenVersionProjection`, which knows nothing about sorting either. */
  useDrivenVersionProjection({ block, data: stored, emit: onUpdate, addVersion: addSortVersion });
  const d = useMemo(() => applySortVersion(stored, editorVersionId), [stored, editorVersionId]);
  /* The TEST surface reads the version the pager points at, which is not always the one being
     edited — a teacher can test version 2 while writing version 3. */
  const shown = useMemo(() => applySortVersion(stored, shownVersionId), [stored, shownVersionId]);
  /* Memoised on the stored data object, as every other block's is: the parent hands every block a
     fresh `blocks` array on any edit, so without this a Sort block re-swept its bin labels whenever
     a teacher typed in an unrelated block. */
  /* `editorVersionId` is in the deps BY NAME, not just the `d` it produced: `block.data` is ONE
     object for the whole bank, so without the pointer the cache answers a question about version 2
     with version 1's issues. Kept out of the call itself — a comment between the arguments hides
     the whole call from the invariant that checks this, which is worse than not having the dep. */
  const issues = memoizedByKey(
    sortIssuesCache, (block.data ?? {}) as object,
    [variables, t, d, editorVersionId, i18n.language],
    () => sortIssues(d, issueCtx),
  );
  /**
   * ONE derivation feeds both the panel and the ring, THROUGH THE SAME POLICY.
   *
   * `issueFieldIds(issues)` on the raw list was the bug: the panel suppressed incompletions while
   * the teacher was still working and the rings did not, so a brand-new block drew a purple ring
   * round every empty item and every unplaced one with nothing on screen explaining why. A
   * highlight with no message is worse than either alone — it says something is wrong and refuses
   * to say what.
   *
   * `error-fg` IS purple in this theme (#A324BA), which is why the symptom reads as a brand colour
   * rather than as an error; the fix is the same either way.
   */
  const { settleLive } = useIssueSettling(
    { equation: d.conditionalEquation },
    { equation: stored.conditionalEquation },
  );
  const issueFields = issueFieldIds(applyIssuePolicy(settleLive(issues), 'editing'));

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  /**
   * THE SIX-STATE FEEDBACK SET, through the shared hook like every other input block.
   *
   * `incomplete_answer` is an EXTRA default here, as it is on FITB — and for the same reason. Both
   * blocks have a genuine partial state that MCQ and Ordering do not: a sort can be half done, and
   * "left three items in the source" is a different thing to say than "got it wrong". Reusing
   * FITB's shared keys rather than minting Sort-specific ones keeps one vocabulary across the two
   * blocks a teacher meets with the same problem.
   */
  const { feedback, setFeedback, resyncFeedback, updateFeedbackState, getTitle } = useInputBlockFeedback({
    initialRaw: (block.data as { feedback?: unknown } | undefined)?.feedback,
    defaults: standardFeedbackDefaults(t, d.gradingMode),
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: d.gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle')
        : d.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
          : t('blocks.feedbackDefaults.anyResponseTitle'),
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
      extra: { incomplete_answer: t('blocks.feedbackDefaults.incompleteAnswerTitle') },
    },
    extraDefaults: sortExtraDefaults(t),
    positiveTrigger: positiveTriggerForMode(d.gradingMode),
    onUpdate: (next) => write({ feedback: next }),
  });
  /* Feedback is the one half of this block's state the hook owns rather than `block.data`, so it
     is the one half an undo cannot restore by re-deriving `d`. The hook no-ops when the normalised
     result is what it already holds, which is what makes this safe to run on every echo of this
     block's own writes as well as on a real undo. */
  useEffect(() => {
    resyncFeedback((block.data as { feedback?: unknown } | undefined)?.feedback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.data]);

  /**
   * FOLLOWING A SCENARIO — the third thing that can own an answer setup.
   *
   * A follower's rows and a numeric branch differ only in what SELECTS them: a variant the leader
   * drew, or a comparison against an equation. Everything downstream reads `activeSetup` and does
   * not care which it got, which is the same indirection Ordering and MCQ settled on.
   *
   * The payload is a whole key plus its own feedback, exactly as a branch carries — so
   * `rowTemplate` seeds from the key on screen, and a version becoming a row carries both.
   */
  const scenario = useScenarioFollower<ScenarioSortBranch>({
    block, blocks, gradingMode: d.gradingMode, feedback, isEditing, isPreview,
    storedData: stored as never,
    restoredRows: d.scenarioBranches,
    initialRows: d.scenarioBranches,
    emit: next => writeStructure(next as unknown as SortBlockDataLocal),
    applyVersion: applySortVersion,
    emitFields: next => write(next as Partial<SortBlockDataLocal>),
    rowTemplate: () => ({ key: d.key, feedback }),
    rowFromVersion: v => ({ key: (v as unknown as SortVersion).key, feedback: (v as unknown as SortVersion).feedback }),
    numeric: {
      read: () => ({ equation: d.conditionalEquation, branches: d.conditionalBranches }),
      write: (equation, br) => write({
        conditionalEquation: equation,
        conditionalBranches: br as ConditionalSortBranch[] | undefined,
      }),
    },
  });

  // ── Branching ───────────────────────────────────────────────────────────────
  /*
   * GRADED ONLY, as every branching surface in the app is. Branches are read straight from stored
   * data, so a block authored with them and later switched to Participation kept showing a
   * scroller above states those branches can never reach.
   */
  const branches = d.conditionalBranches ?? [];
  const showBranching = d.gradingMode === 'graded' && branches.length > 0;
  const branchIndex = Math.min(activeBranchIndex, Math.max(0, branches.length - 1));
  const activeBranch = showBranching ? branches[branchIndex] : undefined;
  /**
   * WHAT THE EDITOR IS ACTUALLY EDITING.
   *
   * A branch carries a whole alternate key AND its own feedback, because what is correct genuinely
   * differs per branch — so what the block says about being correct has to be able to differ too.
   * Every edit below routes through these two, and neither caller needs to know which it hit.
   */
  /* WHICHEVER setup the panel is editing. A scenario row and a numeric branch differ only in
     what selects them, so every read past this line is one path. */
  const activeSetup = scenario.activeScenario ?? activeBranch;
  /**
   * RECONCILED HERE, not just in `initData`.
   *
   * `initData` reconciles the BLOCK key, which covers the unbranched case and nothing else: a
   * branch key and a scenario row key are read straight off stored data, so one that outlived an
   * item or a bin rendered a ghost row in the bin and counted as placed against the completeness
   * check. Doing it at the point of use covers all three with one line, and it is idempotent, so
   * the already-reconciled block key passes through unchanged.
   */
  const activeKey = useMemo(
    () => reconcileKey(activeSetup?.key ?? d.key, d.items, d.bins),
    [activeSetup?.key, d.key, d.items, d.bins],
  );
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  function writeBranches(next: ConditionalSortBranch[], extra: Partial<SortBlockDataLocal> = {}) {
    write({ conditionalBranches: next, ...extra });
  }

  function writeActiveKey(key: SortKey) {
    if (scenario.activeScenario) { scenario.writeActiveRow({ key }); return; }
    if (activeBranch) { writeBranches(branches.map(b => (b.id === activeBranch.id ? { ...b, key } : b))); return; }
    write({ key });
  }

  /**
   * THE SAME ROUTING AS A PATCH, for an edit that changes the key AND something else.
   *
   * `writeActiveKey` is a write of its own, which is right when the key is all that moves. Adding
   * an item moves two fields at once and two writes cannot do it: each one composes a whole block
   * from the data THIS render was given, so the second would be built without the item the first
   * just added and delete it again — the by-omission hazard, one render apart.
   *
   * Marks the row reviewed for the same reason `writeActiveRow` does: filing an item into a bin on
   * a scenario row is deciding that row, not copying it.
   */
  const activeKeyPatch = (key: SortKey): Partial<SortBlockDataLocal> => {
    const row = scenario.activeScenario;
    if (row) {
      return { scenarioBranches: markScenarioReviewed(
        (scenario.rows ?? []).map(r => (r.scenarioId === row.scenarioId ? { ...r, key } : r)),
        row.scenarioId,
      ) };
    }
    if (activeBranch) {
      return { conditionalBranches: branches.map(b => (b.id === activeBranch.id ? { ...b, key } : b)) };
    }
    return { key };
  };

  function setActiveFeedbackStates(states: FeedbackState[]) {
    if (scenario.activeScenario) { scenario.writeActiveRow({ feedback: { states } }); return; }
    if (activeBranch) {
      writeBranches(branches.map(b => (b.id === activeBranch.id ? { ...b, feedback: { states } } : b)));
      return;
    }
    setFeedback({ states });
    write({ feedback: { states } });
  }

  /** A new branch starts from the key on screen — "another answer setup LIKE this one", which is
   *  the same seeding a new version does, for the same reason. */
  const makeBranch = (seedFeedback: SortFeedback): ConditionalSortBranch => ({
    id: crypto.randomUUID(), operator: '==', threshold: '',
    key: activeKey, feedback: { states: seedFeedback.states.map(st => ({ ...st, id: crypto.randomUUID() })) },
  });

  /**
   * EQUATIONS MODE ON AND OFF, and the content flow is deliberately asymmetric in what it keeps.
   *
   * Ported from Ordering's own toggle, which is the standing rule for this control across MCQ,
   * Dropdown and Ordering — so a teacher who has met it once has met it everywhere.
   *
   * ON: media is wiped from every item, because equations mode suppresses media rather than
   * coexisting with it per item. An item's plain text transfers into `formula` only when that text
   * is ALREADY a parseable formula — decided by actually evaluating it against sample values, not
   * guessed from its syntax, so a real sentence and a picture both correctly end up with a blank
   * formula. An item that already has its own authored formula is never overwritten.
   *
   * OFF: each item's current formula is copied back into `text` as real rich content, so a `{name}`
   * becomes an editable chip rather than literal braces. A round trip with nothing authored in
   * between therefore lands BLANK rather than restoring whatever the item said before — prose and
   * media that could not become a formula are a real, accepted loss, and the one a teacher is
   * warned about by the blank showing immediately.
   *
   * `text` is deliberately NOT cleared on the way in: it is what the OFF path has to write back,
   * and what the blank-item check reads so a freshly switched block does not report every item
   * as empty before a single formula has been authored.
   */
  function toggleItemsAsEquations(next: boolean) {
    const names = new Set(variables.map(v => v.name));
    const items = next
      ? d.items.map(i => {
          if (i.formula?.trim()) return { ...i, media: undefined };
          const stripped = stripHtmlForLabel(i.text);
          /* THE ITEM'S OWN mode, not the block's. Angle mode is per field, and this item's
             formula field is the field in question — asking the block would test the transfer in
             a mode the item does not use and could reject a formula that is perfectly good. */
          const transferable = stripped.trim() !== ''
            && evaluateEquation(stripped, minSampleValues(variables), angleModeFor(d, `items.${i.id}.formula`)) !== null;
          return { ...i, media: undefined, formula: transferable ? stripped : '' };
        })
      : d.items.map(i => ({ ...i, text: formulaToRichText(i.formula ?? '', names) }));
    /* The STORED titles too. Flow re-derives its own label from the block, so this is not what
       keeps Flow right — it is what stops a persisted title disagreeing with every live
       derivation of it the moment the mode changes. */
    const retitle = (sts: FeedbackState[]) => sts.map(st => (st.type === 'targeted'
      ? { ...st, title: sortTargetedTitle(st.trigger, items, d.bins, t, next) }
      : st));
    const states = retitle(feedback.states);
    setFeedback({ ...feedback, states });
    /* EVERY SETUP'S TITLES, not just the block's. A branch and a scenario row each carry their own
       feedback, so a targeted card authored inside one kept a title written in the other mode —
       which is the disagreement this resync exists to prevent, one setup over. */
    write({
      items, itemsAsEquations: next, feedback: { ...feedback, states },
      ...(branches.length ? { conditionalBranches: branches.map(b => ({ ...b, feedback: { states: retitle(b.feedback.states) } })) } : {}),
      ...(scenario.rows?.length ? { scenarioBranches: scenario.rows.map(r => ({ ...r, feedback: { states: retitle(r.feedback.states) } })) } : {}),
    });
  }

  function toggleBranching(enabled: boolean) {
    if (enabled) {
      /* Restored from the bank only if THIS SESSION turned it off, so a teacher who unticked it to
         compare does not lose the setup, and a draft left behind by an earlier sitting does not
         come back filing items into bins that have changed since. */
      const draft = bankedThisSessionRef.current ? d.conditionalBranchesDraft : undefined;
      write({
        conditionalEquation: draft?.equation ?? '',
        conditionalBranches: draft?.branches ?? [makeBranch(feedback)],
        conditionalBranchesDraft: undefined,
      });
      setActiveBranchIndex(0);
      return;
    }
    /* Banked under its own key and out of `conditionalBranches` at once — unticking must not be a
       delete, and leaving them live would keep grading against a setup the teacher turned off. */
    const draft = branches.length
      ? { equation: d.conditionalEquation ?? '', branches }
      : d.conditionalBranchesDraft;
    bankedThisSessionRef.current = draft !== undefined;
    write({
      conditionalBranchesDraft: draft,
      conditionalEquation: undefined,
      conditionalBranches: undefined,
    });
  }

  /* Declared here, above the chip derivation that reads it: optional is INERT and shows no
     targeted state at all, and whether any EXIST changes how the catch-all is worded. */
  const targetedStates = d.gradingMode === 'optional'
    ? []
    : activeFeedbackStates.filter(st => st.type === 'targeted');

  const stateFor = (trigger: string): FeedbackState | undefined =>
    feedback.states.find(st => st.type === 'default' && st.trigger === trigger);
  const unansweredState = stateFor('unanswered');
  const incompleteState = stateFor('incomplete_answer');
  const incorrectState = stateFor('incorrect');
  const positiveState = feedback.states.find(st => st.type === 'default'
    && ['correct', 'any_answer', 'any_response'].includes(st.trigger));
  const positiveTriggerLabel = d.gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle')
    : d.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
      : t('blocks.feedbackDefaults.anyResponseTitle');

  /* The live test runs against the SUBMITTED arrangement in submit-gated mode and the live one in
     continuous — the same two-value contract every other block's test surface has. */
  /* A roll is a map of DISPLAY strings; a branch compares numbers. Everything that will not parse
     is dropped rather than coerced to NaN, so a text-valued variable cannot silently make an
     equation unresolvable — the same derivation Ordering's own test data does. */
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1])),
  );
  /**
   * THE ONLY PLACE A FORMULA BECOMES A NUMBER before a student sees it.
   *
   * In plain-chip mode the widget resolves its own chips through the ambient preview provider, so
   * this substitution is redundant-but-harmless. In equations mode there is no such ambient path —
   * the widget renders whatever `.text` it is handed — so this IS the resolution. Mirrors
   * Ordering's own `resolvedItems`. The announcements follow for free: `nameOf` reads the same
   * `.text`, so a listener hears the value that is on screen.
   */
  const resolvedItems = d.itemsAsEquations
    ? d.items.map(i => ({
        ...i,
        text: sortItemDisplayText(i, true, resolvedVariables, i18n.language, angleModeFor(d, `items.${i.id}.formula`)),
      }))
    : d.items;
  /**
   * TWO SURFACES ASKING DIFFERENT QUESTIONS, which one flag was answering for both.
   *
   * `continuous` is the teacher's Continuous Testing switch, and it means "re-evaluate as I move
   * things". PREVIEW HAS NO SUCH SWITCH — it is a student, and a student is evaluated when they
   * submit — but `continuous` defaults to true and nothing in Preview turns it off, so Preview
   * was reading the teacher's setting and following it. Two consequences, both student-facing:
   * feedback appeared LIVE while the student was still arranging, verdict and all, and the
   * arrangement it judged was the live one rather than the submitted snapshot.
   *
   * `liveTest` separates them. In Preview only `hasSubmitted` opens the gate, and only
   * `submittedPlacements` is judged, which is the contract every other block already keeps.
   *
   * THE EMPTY ARRANGEMENT IS STILL EVALUATED, on both surfaces. The gate here used to be
   * `hasPlaced` — is anything in a bin — and on a sort that is the unanswered state itself, so
   * the one arrangement that fires `unanswered` was the one never handed to the evaluator: the
   * legend listed No Answer, greyed, and nothing could light it; submitting an untouched sort in
   * Preview returned no feedback at all. Every other block sets `hasSubmitted` the moment the
   * student acts, and Sort cannot, because its student acts by MOVING something — an untouched
   * block and a deliberately empty one are the same arrangement, so they must be the same answer.
   *
   * `useReportAnswer` keeps its own `hasPlaced` guard: whether to RECORD an attempt is a
   * different question from what the surface displays.
   */
  const liveTest = continuous && !isPreview;
  const answerForTest: SortAnswer = { placements: liveTest ? testPlacements : submittedPlacements };
  const testResult: FeedbackState[] | null = (liveTest || hasSubmitted)
    ? evaluateSort(answerForTest, { ...shown, feedback: shown.feedback }, resolvedVariables)
    : null;
  const firedIds = new Set((testResult ?? []).map(st => st.id));
  /* The draft, evaluated unconditionally: an empty board is No Answer, which is a truthful
     Current rather than the blank Preview showed until the first submit. */
  const draftResult = evaluateSort({ placements: testPlacements }, { ...shown, feedback: shown.feedback }, resolvedVariables);
  const draftFiredIds = new Set(draftResult.map(st => st.id));

  /**
   * THE LEGEND — every state this block can produce, whether or not it fired.
   *
   * Derived from the same `feedback.states` the panel renders, so the chip and the card beside it
   * cannot describe different conditions. Phase 3b adds the targeted chips, which carry two things
   * each ("Civilizations are unstable -> Observations") where Ordering's carry one.
   */
  /**
   * THE LEGEND SAYS THE CONDITION, not the verdict.
   *
   * "Correct" and "Incorrect" are what the colour already says. What a teacher cannot see from the
   * colour is WHICH arrangement each card is waiting for, and for Sort that is enumerable: the key
   * names which items go in which bin. Derived by `sortChips`, which the feedback panel beside it
   * reads too — one derivation, so the two surfaces cannot word the same condition differently.
   */
  /**
   * REACHABILITY BY EXHAUSTION, not by assertion.
   *
   * `sortReachability` enumerates the arrangements and asks `matchesSortTrigger` — the evaluator's
   * own matcher — so "this card can never fire" is a fact rather than a guess. It replaces a line
   * that only knew one case (outside graded, Correct cannot fire) and so said nothing about the
   * one that actually costs a teacher work: a Default Incorrect whose targeted siblings have
   * covered off every wrong arrangement.
   */
  const reach = sortReachability({ gradingMode: d.gradingMode, feedback: { states: activeFeedbackStates }, items: d.items, bins: d.bins }, activeKey);
  const canFire = (trigger: string) => reach.states.find(r => r.trigger === trigger)?.reachable ?? true;
  const chipsFor = (st: FeedbackState) => sortChips(
    st, d.items, d.bins, activeKey, t,
    canFire(st.trigger),
    d.gradingMode, targetedStates.length > 0,
    st.type === 'targeted' ? sortTargetedTitle(st.trigger, d.items, d.bins, t, d.itemsAsEquations) : undefined,
    d.itemsAsEquations,
  );
  const chipCategory = (st: FeedbackState): TestBarChip['category'] =>
    st.type === 'targeted' ? 'targeted'
      : st.trigger === 'unanswered' ? 'unanswered'
        : st.trigger === 'incomplete_answer' ? 'incomplete'
          : st.trigger === 'incorrect' ? 'incorrect'
            : st.trigger === 'any_answer' ? 'any_answer'
              : st.trigger === 'any_response' ? 'any_response'
                : 'correct';
  /**
   * The chips themselves do not depend on what fired -- only `active` does -- so the two
   * surfaces that need them differ by one argument. Behind the Scenes wants the DRAFT's, so
   * Current says what would fire if the student submitted this arrangement now.
   */
  const chipsWithFired = (fired: Set<string>): TestBarChip[] => activeFeedbackStates
    .filter(st => st.type === 'default' || d.gradingMode !== 'optional')
    .flatMap(st => toTestBarChips(chipsFor(st), {
      id: st.id,
      category: chipCategory(st),
      active: fired.has(st.id),
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }));
  const testChips = chipsWithFired(firedIds);

  /**
   * TARGETED STATES — the per-item ones are why this is a block.
   *
   * Mode-scoped the way every other block's are: optional is INERT and shows none at all. Hidden
   * rather than deleted, so flipping back restores what was authored.
   */
  /** Five, matching Ordering: past that the panel stops being readable and the teacher wants a
   *  different question, not a sixth special case. */
  const canAddTargeted = targetedStates.length < 5;

  function writeStates(next: FeedbackState[]) {
    const defaults = activeFeedbackStates.filter(st => st.type !== 'targeted');
    /* Targeted states sit between the no-answer block and the positive one, which is the order
       `normalizeFeedback` itself rebuilds — kept here so a reorder survives a round trip. */
    const unanswered = defaults.filter(st => st.trigger === 'unanswered' || st.trigger === 'incomplete_answer');
    const rest = defaults.filter(st => !unanswered.includes(st));
    setActiveFeedbackStates([...unanswered, ...next, ...rest]);
  }

  function addTargetedState() {
    const used = new Set(targetedStates.map(st => st.trigger));
    const trigger = nextSortTrigger(used, d.items, d.bins);
    const id = crypto.randomUUID();
    setJustAddedStateId(id);
    writeStates([...targetedStates, {
      id, type: 'targeted', trigger,
      title: sortTargetedTitle(trigger, d.items, d.bins, t, d.itemsAsEquations),
      content: '',
      evaluation: d.gradingMode === 'graded' ? 'incorrect' : 'neutral',
    }]);
  }

  const targetedSlot = (
    <DndContext
      sensors={feedbackSensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = targetedStates.findIndex(st => st.id === String(active.id));
        const to = targetedStates.findIndex(st => st.id === String(over.id));
        if (from === -1 || to === -1) return;
        writeStates(arrayMove(targetedStates, from, to));
      }}
      accessibility={dndA11y(t, targetedStates.map(st => st.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(st => st.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(st => (
            <SortableFeedbackCard
              key={st.id}
              sortId={st.id}
              title={sortTargetedTitle(st.trigger, d.items, d.bins, t, d.itemsAsEquations)}
              content={st.content}
              evaluation={st.evaluation}
              startExpanded={st.id === justAddedStateId}
              collapseSignal={d.gradingMode}
              titleLocked
              /* Nothing is correct outside graded, so there is no incorrect to cycle TO. */
              evalLocked={d.gradingMode !== 'graded'}
              triggerZone={(
                <SortTriggerEditor
                  trigger={st.trigger}
                  items={d.items}
                  bins={d.bins}
                  usedKinds={targetedStates.filter(o => o.id !== st.id).map(o => sortTriggerKind(o.trigger))}
                  /* Through the registry, not the raw field: the stored value survives a mode
                     switch and its effect must not, so a Participation block never offers a
                     condition about an order it does not grade. */
                  orderGraded={effectiveSetting('orderWithinBins', d.gradingMode, d.orderWithinBins)}
                  /* The card's own verdict, so the kind picker wears it and changes with the
                     cycler — the distinction `TriggerDropdown` exists to carry. */
                  evaluation={displayEvaluation(d.gradingMode, st.evaluation)}
                  onChange={(trigger) => writeStates(targetedStates.map(o => (o.id === st.id
                    ? { ...o, trigger, title: sortTargetedTitle(trigger, d.items, d.bins, t, d.itemsAsEquations) }
                    : o)))}
                />
              )}
              /* The SHARED chip row, not a hand-rolled chip — `SortTriggerChip` was a fourth
                 copy of a treatment the app already owns, and the legend draws this same list. */
              triggerZoneSummary={<ConditionChipRow chips={chipsFor(st)} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />}
              onTitleChange={() => {}}
              onContentChange={(html) => writeStates(targetedStates.map(o => (o.id === st.id ? { ...o, content: html } : o)))}
              onEvaluationCycle={() => writeStates(targetedStates.map(o => (o.id === st.id
                ? { ...o, evaluation: NEUTRAL_OR_INCORRECT[(NEUTRAL_OR_INCORRECT.indexOf(o.evaluation as 'neutral' | 'incorrect') + 1) % NEUTRAL_OR_INCORRECT.length]! }
                : o)))}
              onDelete={() => writeStates(targetedStates.filter(o => o.id !== st.id))}
              activeEditorRef={sharedToolbar.activeEditorRef}
              setActiveEditor={sharedToolbar.setActiveEditor}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );

  /**
   * THE SAME POINTER, over the feedback panel.
   *
   * A branch carries its own feedback, so without this the panel showed the block's set while the
   * editor was pointed at a branch — the teacher edited one thing and saw another. One index
   * drives both surfaces, which is why it is state on the block rather than inside either.
   */
  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={d.gradingMode}
      branches={branches}
      activeIndex={branchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  /* The key editor, built once: it is rendered bare when there are no alternative setups and
     inside the carousel's envelope when there are. */
  const binsEditor = (
    <SortBinsEditor
      sharedToolbar={sharedToolbar}
      items={d.items}
      bins={d.bins}
      sortKey={activeKey}
      onPlace={place}
      onBinLabelChange={(binId, label) => write({ bins: d.bins.map(b => (b.id === binId ? { ...b, label } : b)) })}
      onBinAdd={() => write({ bins: [...d.bins, { id: newId(), label: '' }] })}
      onBinRemove={removeBin}
      /* A new item lands WHERE IT WAS ADDED. Adding from inside a bin and finding the item in
         the source would make the control a lie about what it just did. */
      onItemAdd={(binId) => {
        const id = newId();
        const items = [...d.items, { id, text: '' }];
        if (!binId || binId === UNSORTED) { write({ items }); return; }
        /* INTO WHATEVER OWNS THE KEY ON SCREEN. This knew about branches and not about scenario
           rows, so adding an item into a bin on a followed Sort filed it in the BLOCK key and the
           row showed it unplaced — the control lying about what it just did, which is the exact
           thing the rule above exists to prevent. */
        write({ items, ...activeKeyPatch(withItemPlaced(activeKey, id, binId)) });
      }}
      onItemRemove={removeItem}
      onItemTextChange={(itemId, text) => write({ items: d.items.map(i => (i.id === itemId ? { ...i, text } : i)) })}
      formulaMode={d.itemsAsEquations}
      onItemFormulaChange={(itemId, formula) => write({ items: d.items.map(i => (i.id === itemId ? { ...i, formula } : i)) })}
      variables={variables}
      rolledValues={rolledValues}
      angleModeFor={(itemId) => angleModeFor(d, `items.${itemId}.formula`)}
      armedBinId={armedBinId}
      setArmedBinId={setArmedBinId}
      armedItemId={armedItemId}
      setArmedItemId={setArmedItemId}
      canAddBin={d.bins.length < MAX_BINS}
      issueFields={issueFields}
      experienceId={experienceId}
      taskId={taskId}
      taskBlocks={blocks}
      /* MERGED, never enumerated: a write listing only the changed field deletes the rest. The
         seeded Label arrives alongside the media because both come from one pick. */
      onMediaChange={(itemId, media, seedLabel) => write({
        items: d.items.map(i => (i.id === itemId
          ? { ...i, media, ...(seedLabel !== undefined ? { text: seedLabel } : {}) }
          : i)),
      })}
    />
  );

  /**
   * THE QUESTION TEXT, which is where every other block looks.
   *
   * This used to count a chip in an ITEM or a BIN LABEL too, reasoning that a sort whose bins are
   * named by a rolled value is a case where the right answer depends on the draw. It is, but that
   * is not the rule the rest of the editor follows, and Sort was the only one of the eight blocks
   * offering this toggle that followed a different one — so an item with a variable in it made a
   * checkbox appear that a teacher had no way to predict, on a block whose question was empty.
   *
   * A variable in an item means the item's CONTENT is computed, which is what `hasVariableInItems`
   * below is for and what Ordering keeps as a separate toggle for the same reason. "What is
   * correct changes with the draw" is a claim about the question, and the question is where the
   * teacher states it.
   */
  const questionVariables = variableChipNames(d.question);
  /* `htmlHasVariableChips`, not `questionVariables.length`, because that is the predicate the
     other seven blocks use — and the two differ on a chip carrying an empty name, which should
     still count as a variable being present. `questionVariables` stays for seeding the equation. */
  const hasBranchableVariable = htmlHasVariableChips(d.question);
  /** A chip in an item, or a formula already naming one — the signal that items are computed. */
  const hasVariableInItems = mayVarMechanism && d.items.some(i => !i.media
    && (htmlHasVariableChips(i.text) || getEquationVariableNames(i.formula ?? '').length > 0));

  /** One locked card, for each of the four states the teacher cannot delete or retrigger. The
   *  evaluation cycle is the one control on them — see the standing evaluation-lock rule. */
  function lockedCard(st: FeedbackState, lockedTriggerLabel: string, cycle: readonly FeedbackState['evaluation'][]) {
    return (
      <FeedbackCard
        fieldId={st.id}
        title={getTitle(st)}
        content={st.content}
        evaluation={st.evaluation}
        locked
        evalLocked={false}
        /* The SAME derivation the legend draws, so a card and the chip beside it cannot describe
           different conditions — which is exactly how the two drifted on Ordering. */
        triggerZone={<ConditionChipRow chips={chipsFor(st)} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />}
        triggerZoneSummary={<ConditionChipRow chips={chipsFor(st)} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />}
        lockedTriggerLabel={lockedTriggerLabel}
        onTitleChange={() => {}}
        onDelete={() => {}}
        onContentChange={html => updateFeedbackState(st.id, { content: html })}
        onEvaluationCycle={() => updateFeedbackState(st.id, {
          evaluation: cycle[(cycle.indexOf(st.evaluation) + 1) % cycle.length]!,
        })}
        activeEditorRef={sharedToolbar.activeEditorRef}
        setActiveEditor={sharedToolbar.setActiveEditor}
      />
    );
  }

  /* The student surface, built once and mounted in three places: the selected card on desktop, the
     phone editor's Test tab, and Preview. One widget, so the three cannot drift. */
  const studentSurface = (
    <SortAnswerDisplay
      question={d.question}
      items={resolvedItems}
      bins={d.bins}
      placements={testPlacements}
      onChange={next => { setTestPlacements(next); if (continuous) setSubmittedPlacements(next); }}
      layout={d.layout}
      inverted={d.inverted}
      itemsAcross={d.itemsAcross}
      /* Through the registry, not the raw field: a Participation block stores the value but does
         not grade order, so it must not speak positions either. */
      orderWithinBins={effectiveSetting('orderWithinBins', d.gradingMode, d.orderWithinBins)}
      rolledValues={rolledValues}
      shuffleGeneration={shuffleGeneration}
      scenario={scenario.ghostedScenario}
    >
      {/**
        * THE FEEDBACK A STUDENT ACTUALLY READS, which this block was never rendering.
        *
        * `testResult` was computed and then used only for the card's border colour and for
        * highlighting which legend chips fired — so the feedback TEXT had nowhere to appear, in
        * the test widget or in Preview. Every other input block hands the same states to the
        * shared `FiredFeedback` as children of its display; Ordering does it on one line.
        */}
      {/* See keyword-block: only the block knows which conditions are in force. */}
      <ReportTestChips chips={chipsWithFired(draftFiredIds)} states={draftResult} />
      <FiredFeedback states={testResult} rolledValues={rolledValues} />
    </SortAnswerDisplay>
  );

  /**
   * THE CARD'S OWN PREVIEW — the arrangement as a student would meet it, filled in with the KEY.
   *
   * Replaces a "4 items, 2 bins" count, which told a teacher the shape of the data and nothing
   * about the question. Ordering's card does the same thing with `OrderingWidgetPreview`, for the
   * same reason: the card answers "how will this look", and a count cannot.
   *
   * `disabled` makes it wholly inert — no carry, no drag, no tap. The key is passed as the
   * placements, so the bins show what belongs in them and the source shows whatever is not placed
   * yet, which is also the clearest possible rendering of an unfinished key.
   */
  const keyPreview = (
    <SortAnswerDisplay
      /* EMPTY on purpose: the card draws the question itself through `BlockQuestion`, which is the
         only thing that renders "No question yet". `question-blank` is flagged `shownInPlace` — a
         claim that this surface shows the gap — and the frame here renders a question only when it
         is non-empty, never a placeholder. Passing it through would show nothing and silently
         break that claim. */
      question=''
      items={resolvedItems}
      bins={d.bins}
      placements={activeKey.placements}
      onChange={() => {}}
      layout={d.layout}
      inverted={d.inverted}
      itemsAcross={d.itemsAcross}
      orderWithinBins={effectiveSetting('orderWithinBins', d.gradingMode, d.orderWithinBins)}
      /* `surfaceRoll`, not `rolledValues`: this one renders on the UNSELECTED card, which is
         display mode. `studentSurface` keeps the record because it only ever appears on an active
         surface — Preview, the selected card, or the phone Test tab, where `isSelected` is false
         and a test is running all the same. */
      rolledValues={surfaceRoll}
      disabled
      scenario={scenario.ghostedScenario}
    />
  );

  /* A chip ANYWHERE the block renders — the question, an item, or a bin label. Bin labels are the
     interesting ones: a bin named by a rolled value makes the whole sort redraw. */
  const hasRollableChips = htmlHasVariableChips(d.question)
    /* In equations mode an item carries no chip at all — it carries a formula, which varies by
       roll just as much. Reading only the chips left Reroll switched off on exactly the blocks
       that most need it. */
    || (d.itemsAsEquations
      ? d.items.some(i => !i.media && !!i.formula?.trim())
      : d.items.some(i => !i.media && htmlHasVariableChips(i.text)))
    || d.bins.some(b => htmlHasVariableChips(b.label));
  const hasRollable = hasRollableVariables(variables);

  /**
   * REROLL DRAWS AGAIN AND LEAVES THE ANSWER ALONE.
   *
   * It used to call `resetTest()` first, which is the one thing a teacher rerolling does not
   * want: the point of pressing it is to answer a question, then draw again and watch whether the
   * NEW numbers still land in the feedback you expected. Clearing the answer throws away the half
   * of that you were testing. MCQ, Dropdown and Ordering have always left the answer in place
   * here; Sort was alone in resetting.
   *
   * Preview is deliberately different and already correct: there Reroll is Reset plus a new draw,
   * because a student's run has no notion of changing the question under a kept answer.
   */
  function handleTestReroll() {
    setShuffleGeneration(g => g + 1);
    setOwnRolledValues(rollVariableValues(variables));
  }

  function resetTest() {
    setTestPlacements({});
    setSubmittedPlacements({});
    setHasSubmitted(false);
    /*
     * NOT re-shuffled, which it was — the one place Reset changed the question.
     *
     * MCQ and Dropdown have the rule written out: Reset means "same question, let me try a
     * different answer", so it must change nothing about the question, including the order
     * things are offered in. Re-shuffling makes a test unrepeatable, because a different
     * outcome could come from the new answer or from the new layout and nothing says which.
     * Sort was the only block that still did it, and emptying the placements already returns
     * every item to the tray — the shuffle on top of that was the question moving.
     *
     * "A fresh student gets a fresh order" is true and belongs to Preview. Reroll is how a
     * teacher asks for a new order on purpose.
     */
  }

  /* A VERDICT BELONGS TO THE VERSION IT WAS TAKEN ON. Without this, sorting version 1 correctly
     and stepping to version 2 left the green card sitting under a different answer key. */
  useTestResetOnVersionChange(shownVersionId, resetTest);

  const testBar = {
    continuous,
    onContinuousChange: (next: boolean) => {
      setContinuous(next);
      // Leaving submit-gated mode publishes what is on screen; entering it withdraws the verdict
      // until they submit, which is the whole point of the mode.
      setSubmittedPlacements(next ? testPlacements : {});
      setHasSubmitted(false);
    },
    onSubmit: () => { setSubmittedPlacements(testPlacements); setHasSubmitted(true); },
    hasResult: continuous ? hasPlaced : hasSubmitted,
    chips: testChips,
    notices: issues.length > 0
      ? <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
      : undefined,
    onReset: resetTest,
    /**
     * REROLL IS ANOTHER STUDENT'S DRAW, not a reset.
     *
     * It re-rolls the variables a bin label or an item carries — which is the whole point on this
     * block, since a sort whose bins are NAMED by a rolled value is a different question each
     * draw — and, for a banked block, re-draws the version too, because that is part of the draw.
     * OFFERED WHENEVER THERE IS ANY RANDOMNESS AT ALL, which on a Sort includes the SHUFFLE.
     *
     * The split with Reset is: Reset returns to the same starting state, Reroll draws again. That
     * only works if everything random has a way to be re-drawn — and the source scramble is random
     * on every block of this type, variables or not. Gating this on variables alone, now that
     * Reset no longer re-scrambles, would leave a plain Sort with no way to see a second
     * arrangement at all.
     *
     * `d.items.length > 1` because one item has no second order to shuffle into.
     */
    onReroll: (hasRollableChips && hasRollable) || d.items.length > 1
      ? handleTestReroll : undefined,
    /* One row for what this test resolved to, shared with every other banked block. */
    topNotices: (hasBank || scenario.testPager) ? (
      <VersionBranchNotice
        versions={scenario.testPager?.versions ?? stored.versions ?? []}
        activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
        onSelectVersion={scenario.testPager?.onSelect ?? setTestVersion}
        noun={scenario.testPager?.noun ?? 'version'}
        /* Stepping a scenario already moves the editor — the pager and the editor share one
           pointer — so Edit has nothing left to move and scrolls to the row instead. Without
           this it is a button that looks live and does nothing visible. */
        editScrollsTo={scenario.activeScenario?.scenarioId
          ? { fieldId: scenario.activeScenario.scenarioId, blockId: block.id }
          : undefined}
        branch={null}
        stacked={isPhone}
        onEdit={() => {
          setEditorVersion(shownVersionId);
          if (onSelect) onSelect();
          else setIsEditing(true);
        }}
      />
    ) : undefined,
  };

  /**
   * ONE WRITER, and every edit goes through it.
   *
   * Phase 1 writes the block's own fields; Phase 5 swaps the body for `writeSortVersion` and every
   * caller here is already shaped for it. That is the whole reason to funnel edits through a single
   * function now rather than calling `onUpdate` from fifteen handlers — the invariant asserting a
   * versioned block never hand-builds a write is what turns that into a guarantee.
   */
  function write(patch: Partial<SortBlockDataLocal>) {
    onUpdate(writeSortVersion(stored, editorVersionId, { ...d, ...patch }));
  }
  /** A write that CHANGES the bank itself, rather than editing inside one of its versions. */
  function writeStructure(next: SortBlockDataLocal) {
    onUpdate(next as unknown as Record<string, unknown>);
  }

  /* Placing is the one edit with a rule in it: `UNSORTED` takes an item OUT of the key rather than
     putting it in a bin called "unsorted", because "not placed" is a state and not a category. The
     completeness check the whole block hangs on depends on that distinction. */
  function place(itemId: string, binId: string, atIndex?: number) {
    writeActiveKey(binId === UNSORTED ? withItemRemoved(activeKey, itemId) : withItemPlaced(activeKey, itemId, binId, atIndex));
  }

  function removeBin(binId: string) {
    /* Items RETURN to unsorted; a bin never owns them. Forced by versions sharing items — see
       `withBinRemoved`, which carries the argument. */
    /* EVERY branch loses the bin, not just the one on screen: bins are shared answer SPACE, so a
       branch keeping a bin the block no longer has would grade against a phantom. */
    write({
      bins: d.bins.filter(b => b.id !== binId),
      ...sweepEverySetup(k => withBinRemoved(k, binId)),
    });
  }

  function removeItem(itemId: string) {
    write({
      items: d.items.filter(i => i.id !== itemId),
      ...sweepEverySetup(k => withItemRemoved(k, itemId)),
    });
  }

  /**
   * EVERY KEY THIS BLOCK HOLDS, not the one on screen.
   *
   * Deleting a bin or an item is STRUCTURAL — it changes the answer space itself, so every setup
   * written against that space has to follow, not just the one being looked at. Branches were
   * swept and scenario rows were not, so a followed Sort kept the deleted item in every row's key:
   * it counted as placed, and the bin drew a blank where it used to be.
   *
   * Versions are deliberately absent: `write` routes through `writeSortVersion`, which puts the
   * key on the version being edited, and the others are reconciled when they are next READ. A
   * version's items genuinely do return to unsorted — see `reconcileKey`.
   */
  function sweepEverySetup(f: (key: SortKey) => SortKey): Partial<SortBlockDataLocal> {
    return {
      key: f(d.key),
      ...(branches.length ? { conditionalBranches: branches.map(b => ({ ...b, key: f(b.key) })) } : {}),
      ...(scenario.rows?.length ? { scenarioBranches: scenario.rows.map(r => ({ ...r, key: f(r.key) })) } : {}),
    };
  }

  /* The student surface, shared by Preview and the selected card's live test — the one canonical
     "what a student sees" layout, as `OrderingAnswerDisplay` is for Ordering. */
  if (isPreview) return studentSurface;

  if (!isEditing) {
    /* GRADED ONLY: the other modes author no key, so "not yet placed" would be reporting an
       absence that is the mode working as intended. The issue check is gated the same way; this
       is the body note that pairs with it. */
    const unplaced = d.gradingMode === 'graded'
      ? d.items.filter(i => !Object.values(d.key.placements).flat().includes(i.id)).length
      : 0;
    return (
      /**
       * THE ROLL, AMBIENT OVER THE WHOLE CARD — which is what makes a chip in a CONDITION read the
       * three-mode rule the same way a chip in the question already did.
       *
       * `AnswerDisplayFrame` provides one, but only inside the answer, so the test bar's legend
       * and the feedback panel sat outside it and printed `{river}` mid-test while the item two
       * inches above showed the rolled number. `null` while unselected is the display mode the
       * card is FOR, and it has to be null rather than `{}` — see BlockPreviewContext's own note.
       */
      <BlockPreviewProvider value={surfaceRoll}>
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={contentRef}
        icon={<Columns3 className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.sort.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={currentMode.label}
        GradingModeIcon={currentMode.Icon}
        gradingModeChipClass={d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted'}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={t('blocks.sort.editBlock')}
        /* The mode bar is `isMain`-gated like every other block's: on desktop the selected card
           already shows the live widget, so the bar would be restating what is on screen. */
        /* The single top-priority issue outranks the mode bar — a teacher scanning the canvas
           needs the problem before the reassurance. */
        answerBarSlot={() => collapsedIssueBar(issues) ?? (isMain ? null : collapsedModeBar(d.gradingMode, t, 'sort'))}
        testEvaluation={testResult ? (testResult[0]?.evaluation ?? 'neutral') : undefined}
        /* While this card is selected it IS the test surface — the shared bar below it, and the
           border coloured by the verdict instead of the selection highlight. */
        testBar={isSelected ? testBar : undefined}
        onActivate={() => (onSelect ? onSelect() : setIsEditing(true))}
      >
        {isSelected ? studentSurface : (
          <>
            {/* THE QUESTION, as every other collapsed card shows it — or "No question yet" where
                there is none. `question-blank` is flagged `shownInPlace`, which is a claim that
                this surface already displays the gap; without this the flag suppressed the issue
                from the status bar and nothing took its place. */}
            <BlockQuestion html={d.question} spaced showEmptyNote />
            {keyPreview}
            {unplaced > 0 && (
              <p className='mt-1 text-sm italic text-ws-muted'>{t('blocks.sort.unplacedNote', { count: unplaced })}</p>
            )}
          </>
        )}
      </CollapsedInputBlock>
      </BlockPreviewProvider>
    );
  }

  return (
    /* The editor is only ever the ACTIVE surface, so its roll is unconditional — the feedback
       panel's chips and the phone Test tab's legend both hang off this. */
    <BlockPreviewProvider value={rolledValues}>
    <InputBlockFrame
      blockId={block.id}
      icon={<Columns3 className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.sort.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.sort.closeEditor')}
      onClose={() => { setIsEditing(false); onClose?.(); }}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      gradingMode={d.gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => write({ gradingMode: nextMode.id })}
      {...angleModeProps}
      sharedToolbar={sharedToolbar}
      /* The renderer opens a block straight onto its Feedback tab when a Flow rule sent the
         teacher here to edit a state. Dropped until now, so that jump landed on the Edit tab. */
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      /* Sticky, so a problem stays readable however far down the key a teacher has scrolled. */
      /* `editing`, not `settled`: an INCOMPLETION is an absence, and a block being built is full
         of absences. The shared policy keeps them quiet until the teacher deselects — which is
         what "done for now" means — and a defect, a genuine contradiction, still speaks at once.
         Unconditional, because the panel already renders nothing when nothing survives policy. */
      warningBarSlot={settleLive(issues).length > 0
        ? <IssuesPanel issues={settleLive(issues)} phase='editing' className='px-4 py-2.5' />
        : null}
      feedbackLabel={t('blocks.areas.feedback')}
      /* PHONE PORTRAIT has no second mount to test in: this editor covers the canvas card whole,
         so without the toggle there is no route back to the widget at all. Same surface and same
         bar as the desktop card, so a teacher is testing one thing on both. */
      testSlot={studentSurface}
      testBar={testBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      feedbackPanelProps={{
        beforeSlot: scenario.feedbackScroller ?? feedbackBranchScroller,
        noAnswerSlot: unansweredState
          ? lockedCard(unansweredState, t('blocks.feedbackDefaults.noAnswerTitle'), NEUTRAL_OR_INCORRECT)
          : null,
        /* The partial state, which Sort shares with FITB and no other block: "left three items in
           the source" is a different thing to say than "got it wrong". */
        partialAnswerSlot: incompleteState
          ? lockedCard(incompleteState, t('blocks.feedbackDefaults.incompleteAnswerTitle'), NEUTRAL_OR_INCORRECT)
          : undefined,
        correctSlot: positiveState
          ? lockedCard(positiveState, positiveTriggerLabel, [positiveState.evaluation])
          : null,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore: canAddTargeted,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
          isEmpty: targetedStates.length === 0,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot: incorrectState
          ? lockedCard(incorrectState, t('blocks.feedbackDefaults.incorrectTitle'), [incorrectState.evaluation])
          : null,
      }}
      settingsSlot={
        /* TWO sections, not one list. `OptionsSection` is the drawer's unit, and the drawer goes
           two-column in phone landscape when one would scroll — which it can only do along a
           section boundary. Grouping also says which choices change what a STUDENT SEES and which
           change what COUNTS, and those are different questions. */
        <>
        <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
          {/* Here rather than beside the key, where a display setting would never go either: this
              is about how the answer is JUDGED. The same placements grade differently with it on
              than off, which is why it belongs to the version, not the block. */}
          <CheckboxRow
            fieldId='orderWithinBins'
            checked={d.orderWithinBins}
            onToggle={() => write({ orderWithinBins: !d.orderWithinBins })}
            label={t('blocks.sort.orderWithinBins')}
            disabled={!settingAvailable('orderWithinBins', d.gradingMode)}
          />
        </OptionsSection>
        <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
          {/* AUTHORED, not responsive. Locking says the axis means something — Paleozoic at the
              bottom — and `responsive` says it does not and the device may decide. Only the teacher
              can know which, which is why this is a setting and not a breakpoint. */}
          <RadioGroup
            fieldId='layout'
            aria-label={t('blocks.sort.layoutLabel')}
            value={d.layout}
            onChange={(layout: SortLayout) => write({ layout })}
            options={LAYOUTS.map(id => ({
              value: id,
              label: t(`blocks.sort.layouts.${id}` as const),
              disabled: id === 'horizontal' && d.bins.length > MAX_HORIZONTAL_BINS,
              /* A DISABLED CONTROL WITH NO REASON is the one case a screen-reader user cannot
                 work out — Ordering says why Circular is unavailable, and this said nothing. */
              describedBy: id === 'horizontal' && d.bins.length > MAX_HORIZONTAL_BINS
                ? 'sort-horizontal-bin-cap' : undefined,
              /**
               * THE ITEMS' OWN AXIS, nested under the layout it belongs to.
               *
               * `layout` is the axis of the BINS and this is the axis of the items inside one, so
               * it reads as a sub-setting of the stacked layout rather than a peer of it. Nested
               * content renders whether or not its option is selected, which is what keeps the
               * setting visible in `responsive` — where it applies on a phone — rather than
               * vanishing and coming back unannounced when the axis changes.
               *
               * Under VERTICAL because that is the only layout it applies to — the indent is the
               * truth, not a visual grouping. Compass lifted its own nested setting OUT because it
               * belonged to all three of its modes; this one belongs to exactly one.
               */
              nestedContent: id !== 'vertical' ? undefined : (
                <CheckboxRow
                  fieldId='itemsAcross'
                  checked={d.itemsAcross}
                  onToggle={() => write({ itemsAcross: !d.itemsAcross })}
                  label={t('blocks.sort.itemsAcross')}
                  /* VERTICAL ONLY, matching what the display does with it exactly — a control
                     reading "off and inert" while the display obeys a stored `true` is the
                     disabled-means-off rule broken from the other side, and the two halves live in
                     different files, so they can only agree by saying the same thing. */
                  disabled={d.layout !== 'vertical'}
                />
              ),
            }))}
          />
          {d.bins.length > MAX_HORIZONTAL_BINS && (
            <p id='sort-horizontal-bin-cap' className='text-xs italic text-ws-muted'>
              {t('blocks.sort.horizontalBinCap', { count: MAX_HORIZONTAL_BINS })}
            </p>
          )}
          {/* EVERY LAYOUT, and the label says which side it means.
              The display has always honoured this in both axes — the container is `flex-row` for
              columns and `flex-col` otherwise, and `inverted` moves the source to the end either
              way — but the control was gated to vertical, so a horizontal sort could not put the
              source on the right at all. "Inverted" alone said nothing about what moves where;
              naming the side is the whole point, and it has to track the layout because the side
              is not the same one. Responsive cannot name a side, because the device picks the
              axis, so it says "last". */}
          <CheckboxRow
            fieldId='inverted'
            checked={d.inverted}
            onToggle={() => write({ inverted: !d.inverted })}
            label={t(d.layout === 'horizontal' ? 'blocks.sort.invertedHorizontal'
              : d.layout === 'vertical' ? 'blocks.sort.invertedVertical'
                : 'blocks.sort.invertedResponsive')}
          />
        </OptionsSection>
        </>
      }
      editingSlot={
        /* `px-6 py-4` and the two headings are the frame's own layout, not decoration: every other
           input block's editing slot is this exact box, and a slot that sets its own spacing sits
           flush to the panel edge while its neighbours do not. */
        <div className='flex flex-col gap-4 px-6 py-4'>
        {/* The three-way offer, when linking meets an existing bank. Rendered from inside the
            editor it is about. */}
        {scenario.remapModal}
        {/* The link control and what it points at, for BOTH kinds of link — see
            `VariesWithSection`, which decides on its own whether a DRIVEN block needs it.
            This was five lines of identical JSX in twelve blocks. */}
        <VariesWithSection
          following={!!scenario.activeScenario}
          linkMenu={scenario.linkMenu}
          stimulus={scenario.editorScenario}
        />
        {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
            the answer setup, because a driven axis varies the question too. Renders its
            children bare when this block is not driven. See `DrivenRowFrame`. */}
        <DrivenRowFrame block={block} data={stored} emit={onUpdate}>
        <EditorSectionHeading
          label={t('blocks.areas.question')}
          divider={!!scenario.activeScenario}
          /* THE PAGER RIDES THE HEADING rather than taking a row of its own. A bank is a niche
             feature, and given its own row it is the first thing the eye lands on. */
          action={(
            <div className='flex items-center gap-2'>
            {/* The bank is GONE while this block follows, not merely inert: versions are many
                stems each with one setup, a scenario is one stem with many, and offering both
                invites building the second structure inside the first. */}
            {!stored.followsBlockId && (
            <VersionSelector
              versions={stored.versions ?? []}
              activeId={editorVersionId}
              onSelect={setEditorVersion}
              onAdd={() => {
                const next = addSortVersion(stored, editorVersionId, () => crypto.randomUUID());
                writeStructure(next);
                /* Land on the one just made: "+" means "write a DIFFERENT question", not "look
                   again at the one I already had". The id is not in `block.data` yet, so the
                   pointer has to be set from the post-change data. */
                const added = next.versions?.[next.versions.length - 1];
                if (added) setEditorVersion(added.id);
              }}
              onRemove={(id) => {
                const next = removeSortVersion(stored, id);
                writeStructure(next);
                setEditorVersion(next.versions?.[0]?.id);
              }}
            />
            )}
            {/* Silent once there is a Varies-With section, which is where the
                control moves to — see `QuestionHeadingLink`. Written as
                `!activeScenario` it knew about scenarios only, so a DRIVEN block
                showed the link twice and the heading overflowed sideways. */}
            <QuestionHeadingLink
              following={!!scenario.activeScenario}
              linkMenu={scenario.linkMenu}
            />
            </div>
          )}
        />
        {/* THE SHARED QUESTION EDITOR, which registers into the frame's one sticky toolbar. A bare
            `RichTextEditor` here drew a SECOND full toolbar directly under the sticky one — two
            identical rows of Bold/Italic/Formula, stacked. */}
        <BlockQuestionEditor
          value={d.question}
          onChange={(question) => write({ question })}
          toolbar={sharedToolbar}
          editorRef={questionEditorRef}
          shouldFocusRef={shouldFocusEditor}
          embedded={embedded}
        />
        <EditorSectionHeading label={t('blocks.areas.answerSetup')} />
        {/* THE TOGGLE ONLY EXISTS ONCE THERE IS SOMETHING TO BRANCH ON. Offering alternative
            answer setups on a block with no variable anywhere is offering a feature that cannot
            do anything. */}
        {d.gradingMode === 'graded' && (hasBranchableVariable || showBranching) && (
          <BranchingCorrectsRow
            fieldId='conditionalCorrectness'
            checked={showBranching}
            onToggle={() => toggleBranching(!showBranching)}
          />
        )}
        {/* GATED ON A VARIABLE IN THE ITEMS, not anywhere in the block.
            Ordering's two toggles are a one-to-one correlation with WHERE the variable lives
            rather than an OR — branching answers "correctness changes with the question", this
            answers "an item's content is computed" — and a variable in the question says nothing
            about the second. Stays offered once on, so it can always be turned back off. */}
        {(hasVariableInItems || d.itemsAsEquations) && (
          <CheckboxRow
            fieldId='itemsAsEquations'
            checked={!!d.itemsAsEquations}
            onToggle={() => toggleItemsAsEquations(!d.itemsAsEquations)}
            label={t('blocks.sort.itemsAsEquationsToggle')}
          />
        )}
        {showBranching ? (
          <BranchConditionCarousel
            seedVariable={questionVariables.length === 1 ? questionVariables[0] : undefined}
            equation={d.conditionalEquation ?? ''}
            onEquationChange={(conditionalEquation) => write({ conditionalEquation })}
            variables={variables}
            rolledValues={rolledValues}
            equationWorks={!!d.conditionalEquation?.trim()}
            hasError={issueFields.has(activeBranch?.id ?? '')}
            branches={branches}
            activeIndex={branchIndex}
            onActiveIndexChange={setActiveBranchIndex}
            onBranchChange={(id, patch) => writeBranches(branches.map(b => (b.id === id ? { ...b, ...patch } : b)))}
            onAdd={() => {
              const next = [...branches, makeBranch(activeBranch?.feedback ?? feedback)];
              writeBranches(next);
              setActiveBranchIndex(next.length - 1);
            }}
            onRemove={(id) => {
              const next = branches.filter(b => b.id !== id);
              writeBranches(next);
              setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
            }}
            onFieldFocus={sharedToolbar.disconnect}
          >
            {/* ONE key editor, nested inside the carousel's own envelope so the equation, the
                condition and the arrangement read as one connected structure. It always shows the
                SAME shared items and bins whichever condition is selected — only their
                ARRANGEMENT changes. There is deliberately never a second read-only list. */}
            {binsEditor}
          </BranchConditionCarousel>
        ) : (
          /* THE SCENARIO'S OWN BOX — the carousel's shape with a variant where the equation goes,
             and nothing at all when this block follows nothing. Without it a teacher could link a
             Sort and then never reach scenario 2 to author its key. */
          <ScenarioSetupFrame
            scenario={scenario.activeScenario}
            name={(scenario.activeScenarioVersion as { name?: string } | null)?.name}
            pager={scenario.pager}
            unreviewedCount={scenario.unreviewedCount}
            onDismissUnreviewed={scenario.dismissUnreviewed}
          >
            {binsEditor}
          </ScenarioSetupFrame>
        )}
        </DrivenRowFrame>
        </div>
      }
    />
    </BlockPreviewProvider>
  );
}
